import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { holdsIdentityAtom, claimsAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import { getSDK } from '../services/sdk-instance';
import type {
  Subscription,
  CreditBalance,
  CreditTransactionsResponse,
  Plan,
  Entitlements,
  OnboardingStatus,
  CompleteOnboardingResponse,
  CurrentPeriodPosition,
  RetryAdvanceChargeResponse,
  ChangePlanRequest,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  ReactivateSubscriptionResponse,
} from '@localagent/sdk';

export function useSubscription() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useQuery<Subscription, Error>({
    queryKey: ['billing', 'subscription', accountId],
    queryFn: () => getSDK().billing.getSubscription(accountId as string),
    enabled: Boolean(isAuthenticated && sdkConfigured && accountId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreditBalance() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useQuery<CreditBalance, Error>({
    queryKey: ['billing', 'credits', 'balance', accountId],
    queryFn: () => getSDK().billing.getCreditBalance(accountId as string),
    enabled: Boolean(isAuthenticated && sdkConfigured && accountId),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useCreditTransactions(params?: { limit?: number; offset?: number }) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useQuery<CreditTransactionsResponse, Error>({
    queryKey: ['billing', 'credits', 'transactions', accountId, params],
    queryFn: () => getSDK().billing.getCreditTransactions(accountId as string, params),
    enabled: Boolean(isAuthenticated && sdkConfigured && accountId),
    staleTime: 2 * 60 * 1000,
  });
}

export function usePlans() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<Plan[], Error>({
    queryKey: ['billing', 'plans'],
    queryFn: () => getSDK().billing.listPlans(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
    staleTime: 10 * 60 * 1000,
  });
}

export function useEntitlements() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useQuery<Entitlements, Error>({
    queryKey: ['billing', 'entitlements', accountId],
    queryFn: () => getSDK().billing.getEntitlements(accountId as string),
    enabled: Boolean(isAuthenticated && sdkConfigured && accountId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useChangePlan() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useMutation<Subscription, Error, ChangePlanRequest>({
    mutationFn: (request) => getSDK().billing.changePlan(accountId as string, request),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['billing'] });
      }
    },
  });
}

export function useOnboardingStatus() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useQuery<OnboardingStatus | null, Error>({
    queryKey: ['billing', 'onboarding', accountId],
    queryFn: async () => {
      const status = await getSDK().billing.getOnboardingStatus(accountId as string);
      // An account that never onboarded reports `not_started`. It is mapped to
      // null because callers gate on truthiness — `status && phase !== 'complete'`
      // would otherwise push a legacy account INTO onboarding.
      return status.phase === 'not_started' ? null : status;
    },
    enabled: Boolean(isAuthenticated && sdkConfigured && accountId),
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      const phase = query.state.data?.phase;
      if (phase === 'complete' || phase === 'canceled' || query.state.data === null) {
        return false;
      }
      return 5 * 1000;
    },
  });
}

export function useCompleteOnboardingPayment() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useMutation<CompleteOnboardingResponse, Error, string>({
    mutationFn: (paymentMethodId: string) =>
      getSDK().billing.completeOnboardingPayment(accountId as string, {
        payment_method_id: paymentMethodId,
      }),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['billing', 'onboarding'] });
        void queryClient.invalidateQueries({ queryKey: ['account', 'product-onboarding'] });
      }
    },
  });
}

export function useCompleteOnboardingComplimentary() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useMutation<CompleteOnboardingResponse, Error, void>({
    mutationFn: () => getSDK().billing.completeOnboardingComplimentary(accountId as string),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['billing', 'onboarding'] });
        void queryClient.invalidateQueries({ queryKey: ['account', 'product-onboarding'] });
      }
    },
  });
}

export function useCurrentPeriod() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useQuery<CurrentPeriodPosition, Error>({
    queryKey: ['billing', 'current-period', accountId],
    queryFn: () => getSDK().billing.getCurrentPeriod(accountId as string),
    enabled: Boolean(isAuthenticated && sdkConfigured && accountId),
    staleTime: 60 * 1000,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'awaiting_payment' || status === 'settlement_failed') {
        return 2_000;
      }
      return 60_000;
    },
  });
}

export function useRetryAdvanceCharge() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useMutation<RetryAdvanceChargeResponse, Error, void>({
    mutationFn: () => getSDK().billing.retryAdvanceCharge(accountId as string),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['billing', 'current-period'] });
      }
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useMutation<CancelSubscriptionResponse, Error, CancelSubscriptionRequest | undefined>({
    mutationFn: (request) => getSDK().billing.cancelSubscription(accountId as string, request),
    onSuccess: (response) => {
      if (!isAuthenticated || !accountId) {
        return;
      }
      // Prefer setQueryData from the mutation payload — a refetch can race and
      // briefly clear cancellation_state before the row is visible to readers.
      queryClient.setQueryData<Subscription>(
        ['billing', 'subscription', accountId],
        (previous) => {
          if (!previous) {
            return previous;
          }
          if (!response.state?.is_pending) {
            throw new Error('cancelSubscription succeeded without pending state');
          }
          return { ...previous, cancellation_state: response.state };
        },
      );
    },
  });
}

export function useReactivateSubscription() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useMutation<ReactivateSubscriptionResponse, Error, void>({
    mutationFn: () => getSDK().billing.reactivateSubscription(accountId as string),
    onSuccess: (response) => {
      if (!isAuthenticated || !accountId) {
        return;
      }
      queryClient.setQueryData<Subscription>(
        ['billing', 'subscription', accountId],
        (previous) =>
          previous
            ? { ...previous, cancellation_state: response.state }
            : previous,
      );
    },
  });
}
